/**
 * Vesper App Constants (Shared)
 *
 * UI-level constants shared between web and mobile.
 * Route constants are platform-specific and NOT included here.
 */

export const APP = {
  NAME: 'Vesper',
  VERSION: '0.1.0',

  GROUP_MIN_SIZE: 2,
  GROUP_MAX_SIZE: 6,

  SCENE_EXPIRY_HOURS: 8,
  DEFAULT_MAP_RADIUS_MILES: 10,

  ARK_MAX_MESSAGE_LENGTH: 500,

  RATING_MIN: 1,
  RATING_MAX: 5,
} as const;

export const AGE_PREFERENCE = {
  MIN: 18,
  MAX_SLIDER: 99,      // slider max position; 99 displays as "Up" (no limit)
  NO_LIMIT_VALUE: 99,   // sentinel: converted to NULL in DB
} as const;

export const GENDER_OPTIONS = [
  { value: 'man' as const,       label: 'Man' },
  { value: 'woman' as const,     label: 'Woman' },
  { value: 'nonbinary' as const, label: 'Nonbinary' },
] as const;

export const INTEREST_OPTIONS = [
  'Hiking', 'Coffee', 'Live music', 'Board games', 'Cooking',
  'Fitness', 'Art galleries', 'Brunch', 'Nightlife', 'Trivia',
  'Outdoor sports', 'Movies', 'Comedy shows', 'Wine / cocktails',
  'Volunteering', 'Tech', 'Reading clubs', 'Dance', 'Travel', 'Yoga',
] as const;

export const HOBBY_OPTIONS = [
  'Photography', 'Guitar', 'Piano', 'Drawing / painting', 'Writing',
  'Coding', 'Gaming', 'Film-making', 'Pottery', 'Woodworking',
  'Knitting', 'Rock climbing', 'Surfing', 'Cycling', 'Running',
  'Cooking / baking', 'Podcasting', 'Vinyl collecting', 'DJing', 'Gardening',
] as const;

/**
 * Vesper personal-interest list — chip options for the "what do you do
 * outside work" question. Curated to skew founder-shaped (climbing, jazz,
 * golf, sailing, fly fishing, …) so chips read true to the audience and
 * Cleo can use them as conversation-starters between matches ("you both
 * climb at the same gym in Williamsburg"). Users can also add up to 6 total
 * with free-text write-ins (24-char limit, enforced in UI/Zod).
 */
export const VESPER_PERSONAL_INTERESTS = [
  'Rock climbing', 'Running', 'Cycling', 'Surfing', 'Sailing',
  'Skiing / snowboarding', 'Tennis', 'Golf', 'Chess', 'Poker',
  'Jazz', 'Classical music', 'Live shows', 'Photography', 'Film',
  'Wine', 'Coffee culture', 'Cooking', 'Reading', 'Fly fishing',
  'Triathlon', 'Yoga', 'Meditation', 'Pilates', 'Pickleball',
] as const;

export const VESPER_PERSONAL_INTERESTS_MAX = 6;
export const VESPER_PERSONAL_INTEREST_WRITE_IN_MAX_LEN = 24;

/**
 * Vesper company / career stage. For founders this is "what stage is your
 * company"; for investors it doubles as a default check-stage focus. Mentors
 * and operators leave it null and instead use careerStageFocus to indicate
 * which stages they want to engage with.
 */
export const VESPER_STAGE_OPTIONS = [
  { value: 'pre_seed' as const, label: 'Pre-seed' },
  { value: 'seed' as const,     label: 'Seed' },
  { value: 'series_a' as const, label: 'Series A' },
  { value: 'series_b' as const, label: 'Series B' },
  { value: 'growth' as const,   label: 'Growth' },
  { value: 'public' as const,   label: 'Public' },
  { value: 'exited' as const,   label: 'Exited / between' },
] as const;

/** Stable mapping of looking-for chip values to display labels. */
export const VESPER_LOOKING_FOR_VALUES = [
  { value: 'co_founder' as const, label: 'Co-founder' },
  { value: 'investors' as const,  label: 'Investors' },
  { value: 'customers' as const,  label: 'Customers / design partners' },
  { value: 'hiring' as const,     label: 'Hiring talent' },
  { value: 'advisors' as const,   label: 'Advisors' },
  { value: 'mentors' as const,    label: 'Mentors' },
  { value: 'mentees' as const,    label: 'Mentees / founders to back' },
  { value: 'peers' as const,      label: 'Peers / mastermind' },
] as const;

/** Vesper roles. */
export const VESPER_ROLE_OPTIONS = [
  { value: 'founder' as const,  label: 'Founder',  blurb: 'Building a company.' },
  { value: 'investor' as const, label: 'Investor', blurb: 'Backing founders.' },
  { value: 'operator' as const, label: 'Operator', blurb: 'Engineer / designer / GTM at a company.' },
  { value: 'mentor' as const,   label: 'Mentor',   blurb: 'Advising or teaching founders.' },
] as const;

/**
 * Vesper sector tags — shown in Onboarding's "What sectors and topics are you
 * working in?" step (replaces the consumer-social INTEREST_OPTIONS list for
 * founder/investor/operator onboarding). Stored in users.interests so existing
 * matching pipeline keeps working without schema change.
 */
export const VESPER_SECTOR_TAGS = [
  'Fintech', 'AI infra', 'Climate', 'B2B SaaS', 'DevTools', 'Health / Bio',
  'Hardware', 'Consumer', 'Crypto', 'Marketplace', 'Media', 'Education',
  'E-commerce', 'Food', 'Real estate', 'Logistics', 'Cybersecurity',
  'Robotics', 'Gaming', 'Future of work',
] as const;

/**
 * Vesper "Looking for" options — shown in Onboarding's repurposed hobbies step.
 * Captures what each user is on Vesper to find. Stored in users.hobbies for
 * v0 (no schema change); a dedicated `users.looking_for` column is logged in
 * vesper-backend-todo.md as a future cleanup.
 */
export const VESPER_LOOKING_FOR_OPTIONS = [
  'Co-founder',
  'Investors',
  'Customers / design partners',
  'Operators (eng / design / GTM)',
  'Mentors',
  'Peers / mastermind',
  'Advisors',
  'Hiring talent',
] as const;

export const ACTIVITY_TYPE_OPTIONS = [
  'Outdoor', 'Social / casual', 'Food & drink', 'Arts & culture',
  'Fitness', 'Music & nightlife', 'Games & puzzles', 'Learning',
] as const;

/**
 * Combined set of all default tags eligible for skill-based slot reservation.
 * Custom user-created tags are excluded from skill matching — only these curated
 * tags can be requested via Ark's skillPreferences extraction.
 */
export const MATCHABLE_TAGS = new Set<string>([
  ...INTEREST_OPTIONS,
  ...HOBBY_OPTIONS,
]);

export const ATMOSPHERE_OPTIONS = [
  { value: 'platonic' as const, label: 'Platonic', description: 'Friendship & social hangouts' },
  { value: 'competitive' as const, label: 'Competitive', description: 'Sports, games & challenges' },
] as const;

// ── Event Seeder Presets ────────────────────────────────────────────────────
//
// Curated buckets for super-organizers seeding founder-relevant public events.
// Each preset combines (a) Eventbrite category IDs to filter at the API level,
// (b) a default keyword phrase, and (c) UI metadata. The post-ingestion
// regex classifier enforces a final quality gate after these run.
//
// Eventbrite reference:
//   - Category 102 = Business & Professional
//     - 1005 Investment, 1007 Networking, 1011 Startups & Small Business
//     - 1010 Sales & Marketing (use sparingly — much of it is non-founder)
//   - Category 113 = Science & Technology
//     - 4001 Biotech, 4002 High Tech
//
// We hold Ticketmaster out of the seeder entirely — its taxonomy is
// entertainment-only (Music/Sports/Theatre) and noise-to-signal is brutal.

// Each preset uses MULTIPLE 1-word keyword variations because Eventbrite's
// Destination Search API caps at ~9-10 results per query and pagination
// returns 400 — the only way to widen the pool is to run several queries
// with different keywords and merge/dedupe. Long multi-term queries get
// 500'd by the API, so each entry must stay 1 word.
//
// The post-ingestion classifier (eventClassifier.ts) is the authoritative
// quality gate after merge.
export const SEEDER_PRESETS = [
  {
    id: 'all_business' as const,
    label: 'All business',
    emoji: '✨',
    description: 'Broad pull — classifier filters to business / founder events',
    eventbriteCategoryIds: ['102', '113'],
    /** Multi-keyword fan-out; merged & deduped, classifier gates final quality. */
    keywords: ['business', 'networking', 'startup', 'founder', 'entrepreneur'],
  },
  {
    id: 'demo_days' as const,
    label: 'Demo days',
    emoji: '🎯',
    description: 'Pitch nights, accelerator showcases, founder demos',
    eventbriteCategoryIds: ['102', '113'],
    keywords: ['demo', 'pitch', 'startup', 'accelerator'],
  },
  {
    id: 'founder_mixers' as const,
    label: 'Founder mixers',
    emoji: '🤝',
    description: 'Founder dinners, coffees, breakfasts, mixers',
    eventbriteCategoryIds: ['102'],
    keywords: ['founder', 'mixer', 'networking', 'entrepreneur'],
  },
  {
    id: 'sector_conferences' as const,
    label: 'Sector conferences',
    emoji: '🎤',
    description: 'Summits, conferences, forums (FinTech, AI, Climate, …)',
    eventbriteCategoryIds: ['102', '113'],
    keywords: ['conference', 'summit', 'forum', 'tech'],
  },
  {
    id: 'investor_events' as const,
    label: 'Investor events',
    emoji: '💸',
    description: 'VC mixers, LP gatherings, fundraising events',
    eventbriteCategoryIds: ['102'],
    keywords: ['investor', 'venture', 'capital', 'fund'],
  },
] as const;

export type SeederPreset = typeof SEEDER_PRESETS[number];
